import type { Step, Option, Lead } from '../types/types';
import { ActionType } from '../types/enums';

/**
 * Step data stored in flow state
 */
export type StepData = {
  option: Option | Option[];
  leadFieldAssociated: keyof Lead;
};

/**
 * Flow state - maps step IDs to their collected data
 */
export interface FlowState {
  [stepId: string]: (Partial<StepData> & Record<string, unknown>) | null;
}

/**
 * Validation error structure
 */
export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Validation result structure
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  resolvedZipData?: {
    stateAbbr: string;
    state: string;
    city: string;
  };
}

/**
 * State shape for the dynamic experience reducer
 */
export interface DynamicExperienceState {
  steps: Step[];
  currentStepIndex: number;
  currentStep: Step;
  currentStepType?: string;
  navigationHistory: Step[];
  flowState: FlowState;
  openModal: boolean;
  acceptTCPA: boolean;
  validationErrors: ValidationError[];
  formStartedFired: boolean;
  isSubmitting: boolean;
  submissionError: string | null;
  popupBlockedUrl: string | null;
}

type Action<T extends string, P = undefined> = P extends undefined
  ? { type: T }
  : { type: T; payload: P };

export type DynamicExperienceAction =
  | Action<ActionType.SET_STEPS, Step[]>
  | Action<ActionType.SET_CURRENT_STEP, Step>
  | Action<ActionType.SET_CURRENT_STEP_INDEX, number>
  | Action<ActionType.SET_NAVIGATION_HISTORY, Step[]>
  | Action<ActionType.ADD_TO_NAVIGATION_HISTORY, Step>
  | Action<ActionType.GO_BACK_IN_HISTORY>
  | Action<ActionType.UPDATE_FLOW_STATE, { stepId: string; data: any }>
  | Action<ActionType.SET_FLOW_STATE, FlowState>
  | Action<ActionType.SET_OPEN_MODAL, boolean>
  | Action<ActionType.SET_ACCEPT_TCPA, boolean>
  | Action<ActionType.SET_VALIDATION_ERRORS, ValidationError[]>
  | Action<ActionType.CLEAR_VALIDATION_ERRORS>
  | Action<ActionType.SET_FORM_STARTED_FIRED, boolean>
  | Action<ActionType.JUMP_TO_STEP, { step: Step; index: number }>
  | Action<ActionType.SET_IS_SUBMITTING, boolean>
  | Action<ActionType.SET_SUBMISSION_ERROR, string | null>
  | Action<ActionType.SET_POPUP_BLOCKED, string | null>
  | Action<ActionType.RESET_FLOW, { entryStep: Step; entryStepIndex: number }>;

/**
 * Reducer for dynamic experience state
 */
export function dynamicExperienceReducer(
  state: DynamicExperienceState,
  action: DynamicExperienceAction,
): DynamicExperienceState {
  switch (action.type) {
    case ActionType.SET_STEPS:
      return {
        ...state,
        steps: action.payload,
      };

    case ActionType.SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.payload,
        currentStepType: action.payload?.type,
      };

    case ActionType.SET_CURRENT_STEP_INDEX:
      return {
        ...state,
        currentStepIndex: action.payload,
      };

    case ActionType.SET_NAVIGATION_HISTORY:
      return {
        ...state,
        navigationHistory: action.payload,
      };

    case ActionType.ADD_TO_NAVIGATION_HISTORY:
      return {
        ...state,
        navigationHistory: [...state.navigationHistory, action.payload],
      };

    case ActionType.GO_BACK_IN_HISTORY: {
      const newHistory = [...state.navigationHistory];
      newHistory.pop();
      const previousStep = newHistory[newHistory.length - 1];

      return {
        ...state,
        navigationHistory: newHistory,
        currentStep: previousStep,
        currentStepType: previousStep?.type,
        currentStepIndex: state.currentStepIndex - 1,
        acceptTCPA: false,
      };
    }

    case ActionType.UPDATE_FLOW_STATE:
      return {
        ...state,
        flowState: {
          ...state.flowState,
          [action.payload.stepId]: action.payload.data,
        },
      };

    case ActionType.SET_FLOW_STATE:
      return {
        ...state,
        flowState: action.payload,
      };

    case ActionType.SET_OPEN_MODAL:
      return {
        ...state,
        openModal: action.payload,
      };

    case ActionType.SET_ACCEPT_TCPA:
      return {
        ...state,
        acceptTCPA: action.payload,
      };

    case ActionType.SET_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: action.payload,
      };

    case ActionType.CLEAR_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: [],
      };

    case ActionType.SET_FORM_STARTED_FIRED:
      return {
        ...state,
        formStartedFired: action.payload,
      };

    case ActionType.JUMP_TO_STEP:
      return {
        ...state,
        currentStep: action.payload.step,
        currentStepIndex: action.payload.index,
        navigationHistory: [action.payload.step],
        acceptTCPA: false,
      };

    case ActionType.SET_IS_SUBMITTING:
      return {
        ...state,
        isSubmitting: action.payload,
      };

    case ActionType.SET_SUBMISSION_ERROR:
      return {
        ...state,
        submissionError: action.payload,
      };

    case ActionType.SET_POPUP_BLOCKED:
      return {
        ...state,
        popupBlockedUrl: action.payload,
      };

    case ActionType.RESET_FLOW: {
      // SAD `navigation.restartFlow` (DB-2.1, EDUN-10689): clear the
      // collected flowState, jump back to the entry step, and reset
      // the navigation history so the user lands on a fresh flow.
      // Validation errors and modal flags reset to mirror a brand-new
      // mount; correlation IDs / `formStartedFired` remain owned by
      // the provider's `useMemo` initializers (FlowStarted will refire
      // when the entry step renders).
      return {
        ...state,
        flowState: {},
        currentStep: action.payload.entryStep,
        currentStepIndex: action.payload.entryStepIndex,
        currentStepType: action.payload.entryStep?.type,
        navigationHistory: [action.payload.entryStep],
        validationErrors: [],
        openModal: false,
        acceptTCPA: false,
        isSubmitting: false,
        submissionError: null,
        popupBlockedUrl: null,
      };
    }

    default:
      return state;
  }
}

/**
 * Action creators for the reducer
 */
export const actionCreators = {
  setSteps: (steps: Step[]): Action<ActionType.SET_STEPS, Step[]> => ({
    type: ActionType.SET_STEPS,
    payload: steps,
  }),

  setCurrentStep: (step: Step): Action<ActionType.SET_CURRENT_STEP, Step> => ({
    type: ActionType.SET_CURRENT_STEP,
    payload: step,
  }),

  setCurrentStepIndex: (
    index: number,
  ): Action<ActionType.SET_CURRENT_STEP_INDEX, number> => ({
    type: ActionType.SET_CURRENT_STEP_INDEX,
    payload: index,
  }),

  setNavigationHistory: (
    history: Step[],
  ): Action<ActionType.SET_NAVIGATION_HISTORY, Step[]> => ({
    type: ActionType.SET_NAVIGATION_HISTORY,
    payload: history,
  }),

  addToNavigationHistory: (
    step: Step,
  ): Action<ActionType.ADD_TO_NAVIGATION_HISTORY, Step> => ({
    type: ActionType.ADD_TO_NAVIGATION_HISTORY,
    payload: step,
  }),

  goBackInHistory: (): Action<ActionType.GO_BACK_IN_HISTORY> => ({
    type: ActionType.GO_BACK_IN_HISTORY,
  }),

  updateFlowState: (
    stepId: string,
    data: any,
  ): Action<ActionType.UPDATE_FLOW_STATE, { stepId: string; data: any }> => ({
    type: ActionType.UPDATE_FLOW_STATE,
    payload: { stepId, data },
  }),

  setFlowState: (
    flowState: FlowState,
  ): Action<ActionType.SET_FLOW_STATE, FlowState> => ({
    type: ActionType.SET_FLOW_STATE,
    payload: flowState,
  }),

  setOpenModal: (
    open: boolean,
  ): Action<ActionType.SET_OPEN_MODAL, boolean> => ({
    type: ActionType.SET_OPEN_MODAL,
    payload: open,
  }),

  setAcceptTCPA: (
    accepted: boolean,
  ): Action<ActionType.SET_ACCEPT_TCPA, boolean> => ({
    type: ActionType.SET_ACCEPT_TCPA,
    payload: accepted,
  }),

  setValidationErrors: (
    errors: ValidationError[],
  ): Action<ActionType.SET_VALIDATION_ERRORS, ValidationError[]> => ({
    type: ActionType.SET_VALIDATION_ERRORS,
    payload: errors,
  }),

  clearValidationErrors: (): Action<ActionType.CLEAR_VALIDATION_ERRORS> => ({
    type: ActionType.CLEAR_VALIDATION_ERRORS,
  }),

  setFormStartedFired: (
    fired: boolean,
  ): Action<ActionType.SET_FORM_STARTED_FIRED, boolean> => ({
    type: ActionType.SET_FORM_STARTED_FIRED,
    payload: fired,
  }),

  jumpToStep: (
    step: Step,
    index: number,
  ): Action<ActionType.JUMP_TO_STEP, { step: Step; index: number }> => ({
    type: ActionType.JUMP_TO_STEP,
    payload: { step, index },
  }),

  setIsSubmitting: (
    isSubmitting: boolean,
  ): Action<ActionType.SET_IS_SUBMITTING, boolean> => ({
    type: ActionType.SET_IS_SUBMITTING,
    payload: isSubmitting,
  }),

  setSubmissionError: (
    error: string | null,
  ): Action<ActionType.SET_SUBMISSION_ERROR, string | null> => ({
    type: ActionType.SET_SUBMISSION_ERROR,
    payload: error,
  }),

  setPopupBlocked: (
    url: string | null,
  ): Action<ActionType.SET_POPUP_BLOCKED, string | null> => ({
    type: ActionType.SET_POPUP_BLOCKED,
    payload: url,
  }),

  resetFlow: (
    entryStep: Step,
    entryStepIndex: number,
  ): Action<
    ActionType.RESET_FLOW,
    { entryStep: Step; entryStepIndex: number }
  > => ({
    type: ActionType.RESET_FLOW,
    payload: { entryStep, entryStepIndex },
  }),
};

export default dynamicExperienceReducer;
